import { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { RefreshCw, AlertTriangle, Calendar, Globe } from "lucide-react";
import { toast } from "sonner";

const IMPACT_COLOR = {
  HIGH:   "txt-loss font-bold",
  MEDIUM: "txt-warn",
  LOW:    "txt-muted",
};

const IMPACT_DOT = {
  HIGH:   "bg-red-500",
  MEDIUM: "bg-yellow-400",
  LOW:    "bg-zinc-500",
};

const CATEGORY_LABEL = {
  monetary_policy: "Monetary Policy",
  inflation:       "Inflation",
  industrial:      "Industrial",
  fiscal_policy:   "Fiscal Policy",
  employment:      "Employment",
  expiry:          "Expiry",
};

const FILTERS = ["ALL", "HIGH", "MEDIUM", "expiry", "monetary_policy", "inflation"];

export default function EconomicCalendar() {
  const [data, setData]     = useState(null);
  const [filter, setFilter] = useState("ALL");
  const [busy, setBusy]     = useState(false);

  const load = async (refresh = false) => {
    setBusy(true);
    try {
      const r = await api.get("/calendar", { params: { refresh, days: 90 } });
      setData(r.data);
    } catch {
      toast.error("Could not load economic calendar");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const events = (data?.events || []).filter((e) => {
    if (filter === "ALL")    return true;
    if (filter === "HIGH")   return e.impact === "HIGH";
    if (filter === "MEDIUM") return e.impact === "MEDIUM";
    return e.category === filter || e.impact === filter;
  });

  // Group by date
  const grouped = events.reduce((acc, e) => {
    (acc[e.date] = acc[e.date] || []).push(e);
    return acc;
  }, {});

  return (
    <AppShell>
      <PageHeader
        overline="Market Intelligence"
        title="Economic Calendar"
        description={`${data?.total || 0} events over next 90 days · ${data?.high_count || 0} high-impact`}
        actions={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => load(true)}
            disabled={busy}
            className="rounded-none border border-[var(--border)] font-section tracking-wider"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${busy ? "animate-spin" : ""}`} />
            REFRESH
          </Button>
        }
      />

      <div className="p-8 space-y-6">
        {/* Upcoming HIGH alert */}
        {data?.upcoming_high && (
          <div className="panel p-4 border-l-2 border-red-500 flex items-center gap-3">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />
            <div>
              <span className="text-xs txt-muted font-section tracking-wider">NEXT HIGH-IMPACT EVENT</span>
              <div className="font-medium text-sm mt-0.5">
                {data.upcoming_high.event}
                <span className="txt-muted ml-2 text-xs">
                  {data.upcoming_high.days_until === 0
                    ? "TODAY"
                    : data.upcoming_high.days_until === 1
                    ? "TOMORROW"
                    : `in ${data.upcoming_high.days_until} days`}
                  {" "}· {data.upcoming_high.date}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-section tracking-wider border ${
                filter === f
                  ? "bg-white text-black border-white"
                  : "bg-transparent txt-secondary border-[var(--border)] hover:text-white"
              }`}
            >
              {CATEGORY_LABEL[f] || f}
            </button>
          ))}
        </div>

        {/* Calendar */}
        {Object.keys(grouped).length === 0 && !busy && (
          <div className="txt-muted text-sm py-8 text-center">No events match the current filter.</div>
        )}

        <div className="space-y-4">
          {Object.entries(grouped).map(([date, dayEvents]) => {
            const dt = new Date(date + "T00:00:00Z");
            const label = dt.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
            const daysUntil = dayEvents[0]?.days_until ?? 0;
            const isToday   = daysUntil === 0;
            const isPast    = daysUntil < 0;

            return (
              <div key={date} className={`panel ${isPast ? "opacity-50" : ""}`}>
                <div className={`px-4 py-2 border-b border-[var(--border)] flex items-center justify-between ${isToday ? "bg-[var(--bg-surface-2)]" : ""}`}>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 txt-muted" />
                    <span className="font-section tracking-wider text-xs">
                      {label}
                      {isToday && <span className="ml-2 text-xs bg-white text-black px-1.5 py-0.5">TODAY</span>}
                    </span>
                  </div>
                  <span className="text-xs txt-muted">
                    {daysUntil > 0 ? `${daysUntil}d away` : isPast ? `${Math.abs(daysUntil)}d ago` : ""}
                  </span>
                </div>
                <div className="divide-y divide-[var(--border)]">
                  {dayEvents.map((e, i) => (
                    <div key={i} className="px-4 py-3 flex items-center gap-4">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${IMPACT_DOT[e.impact] || "bg-zinc-500"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{e.event}</div>
                        <div className="text-xs txt-muted mt-0.5 flex items-center gap-2">
                          <Globe className="w-3 h-3" />
                          {e.country === "IN" ? "India" : "USA"}
                          <span>·</span>
                          {CATEGORY_LABEL[e.category] || e.category}
                        </div>
                      </div>
                      <span className={`text-xs font-section tracking-wider shrink-0 ${IMPACT_COLOR[e.impact] || ""}`}>
                        {e.impact}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AppShell>
  );
}
